import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { epochLabel } from '@/features/projects/format'
import { selectCanWriteProject } from '@/features/projects/slices/projectDetailSlice'
import { useAppDispatch, useAppSelector } from '@/store'
import { projectFileService } from '../services/projectFileService.http'
import {
  deleteFile,
  fetchFiles,
  selectFiles,
  selectFilesDeleting,
  selectFilesListError,
  selectFilesLoading,
  selectFilesUploadError,
  selectFilesUploading,
  uploadFile,
  filesErrorDismissed,
  filesRetryArmed
} from '../slices/projectFilesSlice'

/** Human byte size. */
const sizeLabel = (bytes?: number): string => {
  if (bytes === undefined) return '—'
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`
  if (bytes >= 1_024) return `${Math.round(bytes / 1_024)} kB`
  return `${bytes} B`
}

/**
 * The documents panel (AC-4). Upload is the MULTIPART field-`file` POST (the
 * only upload surface the px backend ships — no signed-URL route exists;
 * spec OQ-1). Download pulls the bytes through the shared transport as a
 * blob — the Bearer rides the interceptor, which a bare `<a href>` could
 * never do — and hands the browser an object URL (revoked right after).
 */
export function ProjectDocumentsPanel({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const files = useAppSelector(selectFiles)
  const loading = useAppSelector(selectFilesLoading)
  const listError = useAppSelector(selectFilesListError)
  const uploading = useAppSelector(selectFilesUploading)
  const uploadError = useAppSelector(selectFilesUploadError)
  const deleting = useAppSelector(selectFilesDeleting)
  const canWrite = useAppSelector(selectCanWriteProject)

  const inputRef = useRef<HTMLInputElement>(null)
  const [downloading, setDownloading] = useState<string | null>(null)
  /** A failed download, surfaced inline (review M-a — never swallowed). */
  const [downloadError, setDownloadError] = useState<string | null>(null)

  useEffect(() => {
    void dispatch(fetchFiles({ orgId, projectId }))
  }, [dispatch, orgId, projectId])

  const onFileChosen = (file: File | undefined): void => {
    if (file === undefined) return
    void dispatch(uploadFile({ orgId, projectId, file })).then(() => {
      // Re-arm the input so choosing the same file twice fires onChange again.
      if (inputRef.current !== null) inputRef.current.value = ''
    })
  }

  const onDownloadClicked = async (fileId: string, name: string): Promise<void> => {
    setDownloading(fileId)
    setDownloadError(null)
    try {
      const blob = await projectFileService.download(orgId, projectId, fileId)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = name
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      // Surfaced, not swallowed — a dead store or expired token is a user
      // problem this panel must own (review M-a).
      const message = error instanceof Error ? error.message : String(error)
      setDownloadError(`${name}: ${message}`)
    } finally {
      setDownloading(null)
    }
  }

  return (
    <section aria-label={t('documents.panelTitle')} className="rounded-lg border border-border bg-card px-4 py-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <SectionLabel>{t('documents.panelTitle')}</SectionLabel>
        {canWrite && (
          <>
            <input
              ref={inputRef}
              id="document-upload"
              type="file"
              className="hidden"
              onChange={(event) => onFileChosen(event.target.files?.[0])}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              data-testid="document-upload-button"
            >
              {uploading ? t('documents.uploading') : t('documents.upload')}
            </button>
          </>
        )}
      </div>

      {downloadError !== null && (
        <p role="alert" className="mt-2 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground" data-testid="download-error">
          {downloadError}
          <button
            type="button"
            onClick={() => setDownloadError(null)}
            className="ml-2 text-xs font-medium text-accent-foreground hover:underline"
          >
            ×
          </button>
        </p>
      )}

      {uploadError !== null && (
        <p role="alert" className="mt-2 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground">
          {t('documents.serverError')}: {uploadError.message}
          <button
            type="button"
            onClick={() => dispatch(filesErrorDismissed())}
            className="ml-2 text-xs font-medium text-accent-foreground hover:underline"
          >
            ×
          </button>
        </p>
      )}

      {listError !== null && (
        <div role="alert" className="mt-2 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground">
          <p>{t('documents.serverError')}: {listError.message}</p>
          <button
            type="button"
            onClick={() => {
              dispatch(filesRetryArmed())
              void dispatch(fetchFiles({ orgId, projectId }))
            }}
            className="mt-1 text-xs font-medium text-accent-foreground hover:underline"
          >
            {t('retry')}
          </button>
        </div>
      )}

      {files.length === 0 ? (
        <p className="mt-1.5 text-sm text-muted-foreground" data-testid="documents-empty">
          {loading ? t('loading') : t('documents.empty')}
        </p>
      ) : (
        <ul className="mt-1.5" data-testid="documents-list">
          {files.map((file) => (
            <li key={file.id} className="v-row flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground" title={file.name}>
                  {file.name}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {sizeLabel(file.size)} · {file.contentType ?? '—'} · {epochLabel(file.createdAt)}
                  {file.pages !== undefined && ` · ${t('documents.pages', { count: file.pages })}`}
                </span>
              </span>
              <span className="flex shrink-0 gap-2 text-xs">
                <button
                  type="button"
                  disabled={downloading === file.id}
                  onClick={() => void onDownloadClicked(file.id, file.name)}
                  className="font-medium text-accent-foreground hover:underline disabled:opacity-50"
                >
                  {downloading === file.id ? t('documents.uploading') : t('documents.download')}
                </button>
                {canWrite && (
                  <button
                    type="button"
                    disabled={deleting === file.id}
                    onClick={() => void dispatch(deleteFile({ orgId, projectId, fileId: file.id }))}
                    className="font-medium text-accent-foreground hover:underline disabled:opacity-50"
                  >
                    {t('documents.delete')}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {!canWrite && <p className="mt-2 text-xs text-muted-foreground">{t('documents.readonly')}</p>}
    </section>
  )
}
